"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getGlobalExceptionOpts = exports.setGlobalExceptionOpts = exports.getGlobalAgentOriginDataForExceptions = exports.setGlobalAgentOriginDataForExceptions = exports.isPlusPlusPlus = exports.PlusPlusPlus = exports.ErrorLevel = void 0;
const json_util_1 = require("../utils/json-util");
const JsErrorAttribs = [
    'name',
    // 'message', removing because now using a getter override for err.message--needs special treatment
    'fileName',
    'lineNumber',
    'columnNumber',
    'stack',
];
let _agentOriginData;
let _globalOpts;
var ErrorLevel;
(function (ErrorLevel) {
    /**
     * Part of the control flow, nothing to worry about.
     * The default when the error is not constructed with a Javascript error.
     */
    ErrorLevel["FlowException"] = "FlowException";
    /**
     * Explicitly set error as a validation error, relating to user data or some other content.
     */
    ErrorLevel["ContentValidation"] = "ContentValidation";
    /**
     * Default.
     */
    ErrorLevel["RegularError"] = "RegularError";
    /**
     * Requires eyes on the problem.
     * The default when the error is constructed with a caught Javascript error.
     * When catching errors that are not severe, you can use setToContentValidationLevel() or setToExceptionLevel() to reduce the level
     */
    ErrorLevel["Severe"] = "Severe";
    /**
     * Notification-worthy. Cannot recover, probably shouldn't try to recover.
     */
    ErrorLevel["Fatal"] = "Fatal";
})(ErrorLevel = exports.ErrorLevel || (exports.ErrorLevel = {}));
// TODO: needs big pass around internalDetails and regular details. Particularly when serializing and hydrating (to and from) errorRecord.
//  And more on internalDetails: origin. jsError / stack.
class PlusPlusPlus extends Error {
    constructor(...args) {
        super('PlusPlusPlus');
        this.isPlusPlusPlus = true;
        this._details = {};
        this._clobberedDetails = [];
        this._internalDetails = {}; // todo: split into private for author vs platform?
        this._internalDetailStack = [];
        this._internalClobberedDetails = [];
        let plusPlusPlus;
        if (args.length && isPlusPlusPlus(args[0])) {
            plusPlusPlus = args.shift();
        }
        else {
            _unxkcdPrototype(this);
            plusPlusPlus = this;
        }
        plusPlusPlus._applyFromArgList(args);
        plusPlusPlus._errorLevel = plusPlusPlus._errorLevel || ErrorLevel.RegularError;
        if (!plusPlusPlus._jsErrorData) {
            plusPlusPlus.setJsErrorInfo(this); // new Error())
            // note: got empty objects when trying stuff like: Error.captureStackTrace(errorData, PlusPlusPlus);
            // also, not sure if it is safe in browser
        }
        return plusPlusPlus;
    }
    _applyFromArgList(initialArgs) {
        initialArgs.forEach((arg) => {
            if (typeof arg === 'string') {
                if (arg.length < 256 && !arg.includes(' ')) {
                    this.setErrorCode(arg);
                }
                else {
                    this.appendDetails({ reason: arg });
                }
            }
            else if (typeof arg === 'number') {
                this.setHttpStatusCode(arg);
            }
            else if (arg && typeof arg === 'object') {
                if (arg.errorCode) {
                    this._hydrateFromErrorRecord(arg);
                }
                else if (!arg.isPlusPlusPlus && arg instanceof Error) {
                    this.setJsErrorInfo(arg);
                }
                else {
                    this.appendDetails(arg);
                }
            }
        });
    }
    toDataRecord(opts = {}) {
        const res = {
            errorCode: this._errorCode,
            details: this._details,
            userMessage: this._userMessage,
            authorMessage: this._authorMessage,
        }; // todo: define/update/use ErrorRecord interface
        if (opts.withInternalDetails) {
            res.internalReason = this._internalReason || '';
            res.internalDetails = this._internalDetails || {};
            if (this._internalDetailStack.length) {
                res.internalDetailStack = this._internalDetailStack;
            }
            if (this._internalClobberedDetails.length) {
                res.clobberedInternalDetails = this._internalClobberedDetails;
            }
            res.httpStatusCode = this._httpStatusCode;
        }
        if (this._clobberedDetails.length) {
            res.clobberedDetails = this._clobberedDetails;
        }
        if (!opts.skipJsError) {
            if (opts.fullJsError) {
                res.jsError = this._jsErrorData;
            }
            else {
                res.jsErrorMessage = (this._jsErrorData || {}).message;
            }
        }
        const origin = this._origin || _agentOriginData;
        if (origin) {
            res.origin = origin;
        }
        if (!opts.skipErrorLevel && this.getErrorLevel() !== ErrorLevel.RegularError) {
            res.errorLevel = this.getErrorLevel();
        }
        return res;
    }
    extData(opts = {}) {
        return this.toDataRecord(Object.assign({ skipJsError: true }, opts));
    }
    dataWithInternalInfo() {
        return this.toDataRecord({
            fullJsError: true,
            withInternalDetails: true,
        });
    }
    toJSON() {
        return this.dataWithInternalInfo(); // todo: restore: extData()
    }
    _hydrateFromErrorRecord(errorRecord) {
        const { errorCode, userMessage, authorMessage, reason, details, internalReason, internalDetails, httpStatusCode, jsError, origin } = errorRecord, rest = __rest(errorRecord, ["errorCode", "userMessage", "authorMessage", "reason", "details", "internalReason", "internalDetails", "httpStatusCode", "jsError", "origin"]);
        this.setErrorCode(errorCode);
        this.setUserMessage(userMessage);
        this.setAuthorMessage(authorMessage);
        const addingDetails = Object.assign({}, details);
        Object.keys(rest).forEach(k => {
            if (k.charAt(0) !== '_' && k !== 'message') {
                addingDetails[k] = rest[k];
            }
        });
        this.appendDetails(Object.assign(Object.assign({}, details), addingDetails));
        this.setJsErrorInfo(jsError);
        this._internalReason = internalReason;
        this._internalDetails = internalDetails;
        this._httpStatusCode = httpStatusCode;
        this._origin = this._origin || origin;
    }
    toString() {
        return this.summary();
    }
    summary() {
        const title = `+++ ${this.getErrorCode() || (this._jsErrorData && this._jsErrorData.message) || 'PlusPlusPlus'} +++`;
        if (!_globalOpts || !_globalOpts.expandMessageForDebug) {
            return title;
        }
        else {
            return `${title}  ${json_util_1.safeStringify(this.dataWithInternalInfo())}`;
        }
    }
    setErrorCode(errorCode, skipIfPresent = true) {
        if (!skipIfPresent || !this._errorCode) {
            this._errorCode = errorCode;
        }
        return this;
    }
    setHttpStatusCode(statusCode, skipIfPresent = true) {
        if (!skipIfPresent || !this._httpStatusCode) {
            this._httpStatusCode = statusCode;
        }
        return this;
    }
    setAuthorMessage(authorMessage, skipIfPresent = true) {
        if (!skipIfPresent || !this._authorMessage) {
            this._authorMessage = authorMessage;
        }
        return this;
    }
    setUserMessage(userMessage, skipIfPresent = true) {
        if (!skipIfPresent || !this._userMessage) {
            this._userMessage = userMessage;
        }
        return this;
    }
    setJsErrorInfo(err, skipIfPresent = true) {
        if (!err || typeof err !== 'object') { // !(err instanceof Error))
            return this;
        }
        if (this._jsErrorData && skipIfPresent && err.message && err.message !== this._jsErrorData.message) {
            this.pushInternalDetail({ jsError: err });
            return this;
        }
        const isPlusPlusPlus = err.isPlusPlusPlus;
        this._jsErrorData = Object.assign(isPlusPlusPlus ? {} : { message: err.message }, _pickAttribs(err, JsErrorAttribs));
        // tmp - broken - todo: rm or restore
        // let stack = this._jsErrorData.stack;
        // if (!stack) {
        //   stack = (new Error('PlusPlusPlus')).stack;
        //   if (stack[1] && stack[1].indexOf('new PlusPlusPlus') >= 0) {
        //     stack.splice(1, 1);
        //   }
        // }
        // if (typeof stack === 'string') {
        //   stack = stack.split('\n');
        // }
        // this._jsErrorData.stack = stack;
        return this;
    }
    clearJsErrorInfo() {
        this._jsErrorData = undefined;
        return this;
    }
    // todo: tbd.. or create separate validationError class?
    // setToContentValidationLevel() {
    //   this._errorLevel = ErrorLevel.ContentValidation;
    // }
    /**
     * Already the default errorLevel, but maybe you want to set it back after something else changed it?
     */
    setErrorLevel(level) {
        this._errorLevel = level;
        return this;
    }
    setToSevereLevel() {
        this.setErrorLevel(ErrorLevel.Severe);
        return this;
    }
    setToFatalLevel() {
        this.setErrorLevel(ErrorLevel.Fatal);
        return this;
    }
    appendDetails(info) {
        if (typeof info === 'object') {
            Object.keys(info).forEach(k => {
                if (this._details[k]) {
                    this._clobberedDetails.push({ [k]: info[k] });
                }
                else {
                    this._details[k] = info[k];
                }
            });
        }
        return this;
    }
    appendInternalDetails(info) {
        Object.keys(info).forEach(k => {
            if (this._internalDetails[k]) {
                this._clobberedDetails.push({ [k]: info[k] });
            }
            else {
                this._internalClobberedDetails[k] = info[k];
            }
        });
        return this;
    }
    setInternalReason(val, skipIfPresent = true) {
        if (!skipIfPresent || !this._internalReason) {
            this._internalReason = val;
        }
        return this;
    }
    pushInternalDetail(item) {
        this._internalDetailStack.push(item);
    }
    getErrorCode() {
        return this._errorCode;
    }
    getErrorLevel() {
        return this._errorLevel;
    }
    getHttpStatusCode() {
        return this._httpStatusCode;
    }
}
exports.PlusPlusPlus = PlusPlusPlus;
function _pickAttribs(src, whitelist) {
    const res = {};
    whitelist.forEach(attrib => {
        if (src.hasOwnProperty(attrib)) {
            res[attrib] = src[attrib];
        }
    });
    return res;
}
function isPlusPlusPlus(val) {
    return val && typeof val === 'object' &&
        (val instanceof PlusPlusPlus || (val.isPlusPlusPlus && typeof val.dataWithInternalInfo === 'function'));
}
exports.isPlusPlusPlus = isPlusPlusPlus;
/**
 * Set global data that gets applied to external errorRecord as "origin".
 * Servers should generally set this on start, including their apiKey.
 * @param data
 */
function setGlobalAgentOriginDataForExceptions(data) {
    _agentOriginData = data;
}
exports.setGlobalAgentOriginDataForExceptions = setGlobalAgentOriginDataForExceptions;
function getGlobalAgentOriginDataForExceptions() {
    return _agentOriginData;
}
exports.getGlobalAgentOriginDataForExceptions = getGlobalAgentOriginDataForExceptions;
function setGlobalExceptionOpts(val) {
    _globalOpts = val;
}
exports.setGlobalExceptionOpts = setGlobalExceptionOpts;
function getGlobalExceptionOpts() {
    return _globalOpts;
}
exports.getGlobalExceptionOpts = getGlobalExceptionOpts;
function _unxkcdPrototype(err) {
    Object.setPrototypeOf(err, PlusPlusPlus.prototype);
    Object.defineProperty(err, 'message', {
        get: function () {
            return err.summary();
        }
    });
}
//# sourceMappingURL=plus-plus-plus.js.map